import DataUsageIcon from '@mui/icons-material/DataUsage';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../hooks/useNumberFormat';
import type { AuthBootstrapSubscriptionSummary } from '../types';

interface TenantMinutesUsageIndicatorProps {
  aiSubscription: AuthBootstrapSubscriptionSummary | null;
  telephonySubscription: AuthBootstrapSubscriptionSummary | null;
}

interface UsageTrack {
  key: 'ai' | 'telephony';
  title: string;
  usedSeconds: number;
  remainingSeconds: number;
  totalSeconds: number;
  percent: number;
  overageSeconds: number;
  barColor: string;
}

const USAGE_THRESHOLD_GRADIENTS = [
  {
    minPercent: 100,
    gradient: 'linear-gradient(90deg, #DC2626 0%, #F87171 100%)',
  },
  {
    minPercent: 95,
    gradient: 'linear-gradient(90deg, #F97316 0%, #FB7185 100%)',
  },
  {
    minPercent: 90,
    gradient: 'linear-gradient(90deg, #FB923C 0%, #FBBF24 100%)',
  },
  {
    minPercent: 85,
    gradient: 'linear-gradient(90deg, #F59E0B 0%, #FDE68A 100%)',
  },
  {
    minPercent: 80,
    gradient: 'linear-gradient(90deg, #A3E635 0%, #FDE047 100%)',
  },
  {
    minPercent: 0,
    gradient: 'linear-gradient(90deg, #22C55E 0%, #86EFAC 100%)',
  },
] as const;

function clampSeconds(value: number | undefined | null): number {
  return Math.max(Number(value || 0), 0);
}

function resolveUsageThresholdGradient(percent: number, overageSeconds: number): string {
  const effectivePercent = overageSeconds > 0 ? 100 : Math.max(Number(percent || 0), 0);
  return (
    USAGE_THRESHOLD_GRADIENTS.find((entry) => effectivePercent >= entry.minPercent)?.gradient
    ?? USAGE_THRESHOLD_GRADIENTS[USAGE_THRESHOLD_GRADIENTS.length - 1].gradient
  );
}

/**
 * Render a compact tenant-wide minutes indicator for the authenticated shell.
 *
 * The layout is intentionally lightweight:
 * - data comes from the auth bootstrap payload that already respects `X-Tenant`;
 * - AI and telephony tracks are only shown when the tenant has finite balance;
 * - the tooltip expands the exact percent/used/remaining details per modality.
 */
export default function TenantMinutesUsageIndicator({
  aiSubscription,
  telephonySubscription,
}: TenantMinutesUsageIndicatorProps) {
  const theme = useTheme();
  const { t } = useTranslation('common');
  const { formatDuration, formatPercent } = useNumberFormat();

  const tracks = useMemo<UsageTrack[]>(() => {
    const nextTracks: UsageTrack[] = [];

    if (aiSubscription && aiSubscription.plan.maxMinutesSeconds !== -1) {
      const usedSeconds = clampSeconds(aiSubscription.usedAiSeconds);
      const remainingSeconds = clampSeconds(aiSubscription.remainingAiSeconds);
      const totalSeconds = usedSeconds + remainingSeconds;
      nextTracks.push({
        key: 'ai',
        title: t('subscription.headerUsage.track.aiTitle'),
        usedSeconds,
        remainingSeconds,
        totalSeconds,
        percent: totalSeconds > 0 ? Math.min((usedSeconds / totalSeconds) * 100, 100) : 0,
        overageSeconds: clampSeconds(aiSubscription.overageAiSeconds),
        barColor: resolveUsageThresholdGradient(
          totalSeconds > 0 ? Math.min((usedSeconds / totalSeconds) * 100, 100) : 0,
          clampSeconds(aiSubscription.overageAiSeconds),
        ),
      });
    }

    if (telephonySubscription) {
      const usedSeconds = clampSeconds(telephonySubscription.usedTelephonySeconds);
      const remainingSeconds = clampSeconds(telephonySubscription.remainingTelephonySeconds);
      const overageSeconds = clampSeconds(telephonySubscription.overageTelephonySeconds);
      const totalSeconds = usedSeconds + remainingSeconds;
      const hasFiniteTelephonyBalance = usedSeconds > 0 || remainingSeconds > 0 || overageSeconds > 0;
      if (hasFiniteTelephonyBalance) {
        nextTracks.push({
          key: 'telephony',
          title: t('subscription.headerUsage.track.telephonyTitle'),
          usedSeconds,
          remainingSeconds,
          totalSeconds,
          percent: totalSeconds > 0 ? Math.min((usedSeconds / totalSeconds) * 100, 100) : 0,
          overageSeconds,
          barColor: resolveUsageThresholdGradient(
            totalSeconds > 0 ? Math.min((usedSeconds / totalSeconds) * 100, 100) : 0,
            overageSeconds,
          ),
        });
      }
    }

    return nextTracks;
  }, [aiSubscription, telephonySubscription, t]);

  if (!tracks.length) {
    return null;
  }

  const isSingleTrack = tracks.length === 1;
  const hasAiAndTelephonyTracks = tracks.some((track) => track.key === 'ai')
    && tracks.some((track) => track.key === 'telephony');

  const tooltipContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {t('subscription.headerUsage.tooltipTitle')}
      </Typography>
      {tracks.map((track) => (
        <Box key={track.key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {track.title}
            {' • '}
            {formatPercent(track.percent)}
          </Typography>
          <Typography variant="caption">
            {t('subscription.headerUsage.contracted')}
            {': '}
            {formatDuration(track.totalSeconds)}
          </Typography>
          <Typography variant="caption">
            {t('subscription.headerUsage.used')}
            {': '}
            {formatDuration(track.usedSeconds)}
          </Typography>
          <Typography variant="caption">
            {t('subscription.headerUsage.remaining')}
            {': '}
            {formatDuration(track.remainingSeconds)}
          </Typography>
          {track.overageSeconds > 0 ? (
            <Typography variant="caption">
              {t('subscription.headerUsage.pendingOverage')}
              {': '}
              {formatDuration(track.overageSeconds)}
            </Typography>
          ) : null}
        </Box>
      ))}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="bottom-end">
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          gap: 1,
          cursor: 'help',
        }}
      >
        <DataUsageIcon
          data-testid="tenant-minutes-usage-icon"
          sx={{
            color: 'text.secondary',
            fontSize: 20,
            flexShrink: 0,
          }}
        />
        <Box
          data-testid="tenant-minutes-usage-root"
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 184,
            maxWidth: 216,
            p: hasAiAndTelephonyTracks ? '5px' : '8px',
            borderRadius: '10px',
            border: '1px solid',
            borderColor: alpha(theme.palette.common.white, 0.28),
            bgcolor: alpha(theme.palette.common.black, 0.28),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: isSingleTrack ? 0 : 0.35,
              justifyContent: isSingleTrack ? 'center' : 'flex-start',
              flex: 1,
              minWidth: 0,
            }}
          >
            {tracks.map((track) => (
              <Box
                key={track.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isSingleTrack ? 1 : 0.75,
                }}
              >
                <Box
                  sx={{
                    minWidth: isSingleTrack ? 24 : 15,
                    width: isSingleTrack ? 24 : 15,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'common.white',
                  }}
                >
                  {track.key === 'ai' ? (
                    <PsychologyIcon
                      data-testid="tenant-usage-track-icon-ai"
                      sx={{ fontSize: isSingleTrack ? 24 : 14, color: 'inherit' }}
                    />
                  ) : (
                    <PhoneInTalkIcon
                      data-testid="tenant-usage-track-icon-telephony"
                      sx={{ fontSize: isSingleTrack ? 20 : 12, color: 'inherit' }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    position: 'relative',
                    flex: 1,
                    height: isSingleTrack ? 12 : 6,
                    borderRadius: 999,
                    overflow: 'hidden',
                    bgcolor: alpha(theme.palette.common.white, 0.12),
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.max(Math.min(track.percent, 100), track.percent > 0 ? 6 : 0)}%`,
                      height: '100%',
                      borderRadius: 999,
                      background: track.barColor,
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
}
